import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { clarionDoorURL } from '../../../../constants/external-links';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { policyType as policyTypeLookup } from '@ourbranch/lookups';
import { IconButton } from '@material-ui/core';

import { withStore } from 'core/store';
import NotificationIcon from 'core/assets/svg/alert-dark-green-bold.svg';
import InfoIcon from 'core/assets/svg/ic-help.svg';
import { Tooltip } from 'core/components/tooltip';
import ValueField from 'core/components/value-field';
import equalsIcon from 'core/assets/svg/equals.svg';
import timesIcon from 'core/assets/svg/times.svg';
import { Card } from 'core/components/card';
import { capitalizeWords, currencyFormatter, dateFormatter } from 'core/helpers/formatters';
import { AutoBreakdownPreview } from 'common/components/auto/auto-breakdown-preview';
import { useToast } from 'core/components/toast';
import { GET_RATING_WORKSHEET } from 'core/store/offer-store/offer-queries';

import useSession from 'core/hooks/use-session';
import useStyles from '../policy-sidebar.styles';

const avoidKeys = ['startDate', 'segmentId', '__typename', 'clarionDoorId', 'farmClarionDoorIds'];

const getChanges = (object) => {
  const keys = Object.keys(object);
  return keys
    .filter((key) => !avoidKeys.includes(key) && !!object[key])
    .map((key) => capitalizeWords(key))
    .join(', ');
};

function getFarmClarionDoorIds(farmClarionDoorIds) {
  const ids = [];
  if (farmClarionDoorIds?.liabilityClarionDoorId) {
    ids.push(farmClarionDoorIds.liabilityClarionDoorId);
  }
  if (farmClarionDoorIds?.risksClarionDoorIds) {
    for (const risk of farmClarionDoorIds.risksClarionDoorIds) {
      ids.push(risk.clarionDoorId);
    }
  }
  return ids;
}

const SegmentItem = ({
  store: {
    account: {
      policies: {
        policy: {
          switchSegment,
          downloadAutoPremiumBreakdownPdf,
          downloadingAutoPremiumBreakdown,
          preview,
          policy: { offer, policyDetails, premiumBreakdown, id: policyId, accountId: policyAccountId }
        }
      }
    }
  },
  segment,
  policyType,
  active,
  lastChild,
  onClick,
  isDraft,
  isNewChange
}) => {
  const classes = useStyles({ isDraft, isInPolicyPreviewMode: !!preview });
  // accountId is the first part of the policy ID (format: accountId-policyNumber)
  const accountId = policyAccountId || (policyId ? policyId.split('-')[0] : null);
  const toast = useToast();
  const { isAgency, canApproveUWReview } = useSession();
  const [fetchRatingWorksheet] = useLazyQuery(GET_RATING_WORKSHEET, { fetchPolicy: 'no-cache' });
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);

  const farmClarionDoorIds = useMemo(() => getFarmClarionDoorIds(segment.farmClarionDoorIds), [segment]);

  useEffect(() => {
    if (active) {
      switchSegment(segment.segmentId);
    }
  }, [active, switchSegment, segment.segmentId]);
  const draftLabel = isNewChange ? 'Draft for new change' : 'Draft for existing change';
  const label = isDraft
    ? `${draftLabel} ${dateFormatter(segment.startDate)}`
    : `Start Date ${dateFormatter(segment.startDate)}`;
  const changes = useMemo(() => {
    return getChanges(segment);
  }, [segment]);

  const timelineTracker = classNames([
    classes.segmentTimelineTracker,
    { [classes.lastSegmentTimelineTracker]: lastChild },
    { [classes.segmentTimelineTrackerDraft]: isDraft }
  ]);
  const listItem = classNames([classes.segmentsListItem, classes.versionsListItem]);
  const circleIcon = classNames([classes.dot, classes.timelineDot, classes.segmentDot]);

  const handleDownloadFarmWorksheet = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDownloadingWorksheet(true);
      try {
        const { data, error } = await fetchRatingWorksheet({
          variables: { clarionDoorIds: farmClarionDoorIds }
        });
        if (error) {
          throw error;
        }
        if (data?.getRatingWorksheet) {
          window.open(data.getRatingWorksheet, '_blank');
        }
      } catch {
        toast.notify({ type: 'error', message: 'Failed to download rating worksheet. Try again.' });
      } finally {
        setDownloadingWorksheet(false);
      }
    },
    [farmClarionDoorIds, fetchRatingWorksheet, toast]
  );

  const changePremiumFormatter = useCallback(
    (value) => {
      // Non-farm: existing ClarionDoor portal link
      if (segment.clarionDoorId) {
        return (
          <a
            className={classes.clarionDoorLink}
            href={`${clarionDoorURL}${segment.clarionDoorId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        );
      }

      // Farm: download rating worksheet (internal staff + UW approvers)
      if ((!isAgency || canApproveUWReview) && farmClarionDoorIds.length > 0) {
        return (
          <a className={classes.clarionDoorLink} href="#" onClick={handleDownloadFarmWorksheet}>
            {downloadingWorksheet ? 'Downloading...' : value}
          </a>
        );
      }

      return value;
    },
    [
      segment,
      classes,
      farmClarionDoorIds,
      handleDownloadFarmWorksheet,
      downloadingWorksheet,
      isAgency,
      canApproveUWReview
    ]
  );

  const handleDownloadPdf = useCallback(async () => {
    const result = await downloadAutoPremiumBreakdownPdf(policyId, accountId, segment.segmentId);

    if (result.error) {
      toast.notify({ type: 'error', message: result.error });
    } else {
      toast.notify({ type: 'success', message: 'PDF generated successfully' });
    }
  }, [downloadAutoPremiumBreakdownPdf, policyId, accountId, segment.segmentId, toast]);

  const handleClick = () => {
    // Can't navigate to other segments in policy preview mode
    if (!!preview && !isDraft) {
      return;
    }

    onClick();
  };

  return (
    <li className={listItem}>
      {lastChild ? (
        <div className={classes.segmentsBottomWrapper}>
          <IconButton
            color="secondary"
            onClick={handleClick}
            className={classNames(circleIcon, { [classes.activeDot]: active })}
          />
          <span className={classes.segmentsBottomLine} />
        </div>
      ) : (
        <div className={timelineTracker}>
          <IconButton
            color="secondary"
            onClick={handleClick}
            className={classNames(circleIcon, { [classes.activeDot]: active })}
          />
        </div>
      )}

      <div className={classes.segmentContent} onClick={handleClick}>
        <h4 className={classes.timelineSubtitle}>{label}</h4>
        <Card
          key={segment.segmentId}
          className={classNames([
            classes.vItemCard,
            classes.vItemSegment,
            { [classes.activeCard]: active },
            { [classes.vItemCardDraft]: isDraft }
          ])}
        >
          {isDraft && (
            <div className={classes.draftHeader}>
              <div className={classes.draftHeaderLeft}>
                <img src={NotificationIcon} className={classes.draftInfoIcon} alt="Notification Icon" />
                <span className={classes.draftHeaderText}>This is a draft</span>
              </div>
              <Tooltip
                text="This draft hasn't been finalized. It's either a fresh change or a revision of an existing one."
                smallIcon={{ src: InfoIcon }}
                className={classes.draftHelpIcon}
                placement="top"
              />
            </div>
          )}
          <div className={isDraft ? classes.draftCardContent : undefined}>
            {changes ? (
              <ValueField className={classes.valueFieldContent} label="Items Changed" value={changes} />
            ) : (
              'Initial Segment'
            )}
            <ValueField
              className={classes.valueFieldContent}
              label="Policy Premium"
              value={currencyFormatter(segment.premium)}
            />
            <ValueField
              className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
              label="Daily Rate"
              value={currencyFormatter(segment.dailyRate)}
            />
            <img src={timesIcon} alt="Times" className={classes.timesIcon} />
            <ValueField
              className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
              label="Days"
              value={segment.segmentDays}
            />
            <img src={equalsIcon} alt="Equals" className={classes.equalsIcon} />
            <ValueField
              className={classNames(classes.valueFieldInline, classes.valueFieldContent)}
              label="Change Premium"
              formatter={changePremiumFormatter}
              value={currencyFormatter(segment.segmentPremium)}
            />
            {policyType === policyTypeLookup.Auto && policyDetails && policyDetails.cars && (
              <AutoBreakdownPreview
                triggerText="Premium Breakdown"
                offer={offer}
                policyType={policyType}
                autoCoverage={policyDetails.autoCoverage || offer?.quote?.autoCoverage}
                premiumBreakdown={JSON.parse(premiumBreakdown || '{}')}
                cars={policyDetails.cars}
                trailers={policyDetails.trailers}
                onDownloadPdf={handleDownloadPdf}
                downloadingPdf={downloadingAutoPremiumBreakdown}
              />
            )}
          </div>
        </Card>
      </div>
    </li>
  );
};

SegmentItem.propTypes = {
  segment: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  lastChild: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  policyType: PropTypes.string.isRequired,
  isDraft: PropTypes.bool
};

export default withStore(SegmentItem);
